import { Directive, HostAttributeToken, inject, input, signal } from '@angular/core';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { BaseNumberInputDirective } from './base-number-input.directive';
import { NumberInputService } from './number-input.service';

import type {
  OnChanges,
  SimpleChanges
} from '@angular/core';
import type {
  ControlValueAccessor
} from '@angular/forms';

import type { FormatParameters } from './input.domain';

/**
 * Number Input Directive
 *
 * Allows an input type number to be formatted on blur
 *
 * @example
 * <input type="number" tdWbNumberInput="n4-2">
 */
@Directive({
  selector: 'input[tdWbNumberInput]',
  providers: [ValueAccessorProviderFactory(NumberInputDirective)],
  host: {
    '(input)': 'onInput($event)',
    '(keydown)': 'onKeyDown($event)',
    '(paste)': 'onPaste($event)',
    '(focus)': 'onFocus()',
    '(blur)': 'onBlur()'
  }
})
export class NumberInputDirective extends BaseNumberInputDirective implements ControlValueAccessor, OnChanges {
  protected readonly numberInputFormatService = inject<NumberInputService>(NumberInputService);

  private static readonly DEFAULT_FORMAT: string = 'n0';

  readonly format = input<string>(undefined, { alias: 'tdWbNumberInput' });
  protected readonly _effectiveFormat = signal<string>(NumberInputDirective.DEFAULT_FORMAT);

  constructor() {
    const groupSeparator = inject(new HostAttributeToken('tdWbNumberInputGroupSeparator'), { optional: true });
    super(groupSeparator as 'true' | 'false' | null);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.format !== undefined) {
      const currentValue = changes.format.currentValue;
      this._effectiveFormat.set(
        currentValue === undefined || currentValue === '' ? NumberInputDirective.DEFAULT_FORMAT : currentValue
      );
      super.onChanges();
    }
  }

  protected formatValue(value: number): string {
    return this.numberInputFormatService.format(value, this.getFormat());
  }

  protected parseValue(value: string): number {
    const { format, decimalTruncate }: { format?: string, decimalTruncate?: boolean } = this.getFormat();
    return this.numberInputFormatService.parse(value, format, decimalTruncate);
  }

  protected getFormat(): FormatParameters {
    let format = this._effectiveFormat();
    if (!format) {
      format = this.format() ?? NumberInputDirective.DEFAULT_FORMAT;
    }
    let decimalTruncate = false;
    const noGroupSeparator = this.focused || !this.groupSeparator;
    const editFormat = this.focused;
    if (this.numberInputFormatService.isCurrencyFormat(format)) {
      if (this.focused) {
        format = `n${format.substring(1)}`; // switch to numeric format when focused - no currency symbol.
      }
    } else if (this.numberInputFormatService.isDecimalTruncate(format)) {
      decimalTruncate = true;
      format = `n${format.substring(1)}`;
    }
    return { format, noGroupSeparator, editFormat, decimalTruncate };
  }
}
